import * as z from 'zod/mini';

import type { useObservability } from '../../helpers/observability';
import { impersonationSession } from '../account/impersonation';
import { getAnonymousId } from '../shared/trackers';
import { CosmosOnboardingZeroArmDocument } from './__generated__/onboarding-zero-arm.zeus';
import { requestZeusReadFromBrowser } from './zeusBrowserClient';

const ONBOARDING_ZERO_FLAG = 'acq-26-10-onboarding-zero-prelim';
const ONBOARDING_ZERO_ARM = 'onboarding_zero_revamped_prelim';
const armSchema = z.enum(['control', 'revamped_prelim_only', ONBOARDING_ZERO_ARM]);
const ARM_READ_TIMEOUT_MS = 1500;

async function readArm(addressId: string) {
  const identity = z.uuid().safeParse(getAnonymousId());
  if (!identity.success || impersonationSession.isImpersonatingSession()) return null;
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<null>((resolve) => {
    timer = setTimeout(() => {
      controller.abort();
      resolve(null);
    }, ARM_READ_TIMEOUT_MS);
  });
  const read = requestZeusReadFromBrowser(
    CosmosOnboardingZeroArmDocument,
    {
      input: { keys: [ONBOARDING_ZERO_FLAG], grain: { customer: { anonymousId: identity.data } } },
      addressId,
    },
    controller.signal,
  ).then(({ experiments, viewer }) => {
    const result = experiments.evaluations;
    if (
      viewer.session?.isImpersonated ||
      result?.__typename !== 'ExperimentsQueryEvaluationsSuccess'
    ) {
      return null;
    }
    const assignment = result.data.find(({ key }) => key === ONBOARDING_ZERO_FLAG);
    const arm = armSchema.safeParse(assignment?.value);
    return assignment && arm.success ? { ...assignment, value: arm.data } : null;
  });
  try {
    return await Promise.race([read, timeout]);
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

const readsByAddress = new Map<string, ReturnType<typeof readArm>>();
const impressedAddresses = new Set<string>();

// Cake honors the same QA pin, so pinned sessions must not enter the readout.
function pinnedArm(): string | undefined {
  try {
    return window.localStorage.getItem(`${ONBOARDING_ZERO_FLAG}.reception.od`)?.trim() || undefined;
  } catch {
    return undefined;
  }
}

/** A failed or slow read keeps the modal. Prefetching does not record an exposure. */
export async function isOnboardingZeroAddress(addressId: string): Promise<boolean> {
  const pinned = pinnedArm();
  if (pinned) return pinned === ONBOARDING_ZERO_ARM;
  const key = addressId.toLowerCase();
  let read = readsByAddress.get(key);
  if (!read) {
    read = readArm(key);
    readsByAddress.set(key, read);
  }
  return (await read)?.value === ONBOARDING_ZERO_ARM;
}

/** Called only when the resolved arm decides whether to show the modal. */
export async function impressOnboardingZeroAddress({
  addressId,
  trackJourneyEvent,
}: {
  addressId: string;
  trackJourneyEvent: ReturnType<typeof useObservability>['trackJourneyEvent'];
}): Promise<void> {
  const key = addressId.toLowerCase();
  const assignment = await readsByAddress.get(key);
  if (
    pinnedArm() ||
    impersonationSession.isImpersonatingSession() ||
    !assignment ||
    assignment.variationId === null ||
    impressedAddresses.has(key)
  )
    return;
  impressedAddresses.add(key);
  trackJourneyEvent('experimentation', 'impression', {
    experimentName: ONBOARDING_ZERO_FLAG,
    variant: assignment.value,
    variationId: assignment.variationId,
    addressToken: key,
  });
}
